import type { TithiLanguage } from "../tithi-today.copy";

type TithiTypeKey = "Nanda" | "Bhadra" | "Jaya" | "Rikta" | "Poorna";

const TITHI_TYPE_ORDER: readonly TithiTypeKey[] = ["Nanda", "Bhadra", "Jaya", "Rikta", "Poorna"];

const TITHI_TYPE_COPY = {
  en: {
    Nanda: {
      label: "Nanda Tithi",
      desc: "Joyful — traditionally used for celebration, social activity, and beginnings",
    },
    Bhadra: {
      label: "Bhadra Tithi",
      desc: "Steady — traditionally considered supportive for ceremonies and decisions",
    },
    Jaya: {
      label: "Jaya Tithi",
      desc: "Victorious — traditionally linked with courage and resolving obstacles",
    },
    Rikta: {
      label: "Rikta Tithi",
      desc: "Empty — traditionally used for focused worship rather than major beginnings",
    },
    Poorna: {
      label: "Poorna Tithi",
      desc: "Complete — traditionally associated with completion and fulfilment",
    },
  },
  hi: {
    Nanda: {
      label: "नंदा तिथि",
      desc: "आनंददायक — परंपरा में उत्सव, मेल-जोल और शुरुआत से जुड़ी",
    },
    Bhadra: {
      label: "भद्रा तिथि",
      desc: "स्थिर — परंपरा में संस्कार और महत्वपूर्ण निर्णयों से जुड़ी",
    },
    Jaya: {
      label: "जया तिथि",
      desc: "विजयी — परंपरा में साहस और बाधा सुलझाने से जुड़ी",
    },
    Rikta: {
      label: "रिक्ता तिथि",
      desc: "रिक्त — परंपरा में बड़ी शुरुआत के बजाय विशेष पूजा से जुड़ी",
    },
    Poorna: {
      label: "पूर्णा तिथि",
      desc: "पूर्ण — परंपरा में पूर्णता और फल-प्राप्ति से जुड़ी",
    },
  },
} as const;

const TITHI_TYPE_TONES: Readonly<Record<TithiTypeKey, { badgeClass: string; dotClass: string }>> = {
  Nanda: {
    badgeClass: "border-success/30 bg-success/10 text-success",
    dotClass: "bg-success",
  },
  Bhadra: { badgeClass: "border-info/30 bg-info/10 text-info", dotClass: "bg-info" },
  Jaya: {
    badgeClass: "border-warning/30 bg-warning/10 text-warning",
    dotClass: "bg-warning",
  },
  Rikta: {
    badgeClass: "border-ish-accent-border bg-ish-accent-light text-intent-brand",
    dotClass: "bg-ish-accent",
  },
  Poorna: {
    badgeClass: "border-ritual-gold/30 bg-surface-warm text-ish-dark",
    dotClass: "bg-ritual-gold",
  },
};

export interface TithiTypeInfo {
  label: string;
  desc: string;
  badgeClass: string;
  dotClass: string;
}

function getTithiTypeKey(tithiNumber: number): TithiTypeKey {
  const numberInPaksha = ((Math.max(1, tithiNumber) - 1) % 15) + 1;
  return TITHI_TYPE_ORDER[(numberInPaksha - 1) % TITHI_TYPE_ORDER.length];
}

function getTithiTypeInfoForKey(key: TithiTypeKey, language: TithiLanguage): TithiTypeInfo {
  return { ...TITHI_TYPE_COPY[language][key], ...TITHI_TYPE_TONES[key] };
}

export function getTithiTypeInfo(tithiNumber: number, language: TithiLanguage): TithiTypeInfo {
  return getTithiTypeInfoForKey(getTithiTypeKey(tithiNumber), language);
}

export default function TithiTypesReference({ language = "en" }: { language?: TithiLanguage }) {
  const title = language === "hi" ? "तिथि के 5 प्रकार" : "The 5 Types of Tithi";

  return (
    <div className="card-sacred mb-6 p-5">
      <h3 className="mb-4 text-body font-bold text-ish-dark">{title}</h3>
      <div className="space-y-3">
        {TITHI_TYPE_ORDER.map((key) => (
          <TithiTypeReferenceItem key={key} info={getTithiTypeInfoForKey(key, language)} />
        ))}
      </div>
    </div>
  );
}

function TithiTypeReferenceItem({ info }: { info: TithiTypeInfo }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${info.dotClass}`} />
      <div>
        <p className="text-body-small font-semibold text-ish-dark">{info.label}</p>
        <p className="text-caption text-ish-text-faint">{info.desc}</p>
      </div>
    </div>
  );
}
